// Tabla filtrable de gastos. Filtros: fecha desde/hasta, categoría, status.
// Cuando el row tiene receipts, mostramos thumbnail (image) o ícono PDF.

import { useState } from 'react';

import { useCategories, useExpenses } from '../hooks/useExpenses';
import type { ExpenseListFilters, ExpenseStatus, Receipt } from '../types';

const STATUS_LABEL: Record<ExpenseStatus, string> = {
  draft: 'Borrador',
  recorded: 'Registrado',
  paid: 'Pagado',
  void: 'Anulado',
};

interface Props {
  defaultFilters?: ExpenseListFilters;
}

function ReceiptThumb({ receipt }: { receipt: Receipt }) {
  const isImage = receipt.mime_type.startsWith('image/');
  if (isImage) {
    return (
      <a href={receipt.url} target="_blank" rel="noreferrer" title={receipt.original_filename}>
        <img
          src={receipt.url}
          alt={receipt.original_filename}
          className="w-10 h-10 rounded object-cover border"
        />
      </a>
    );
  }
  return (
    <a
      href={receipt.url}
      target="_blank"
      rel="noreferrer"
      title={receipt.original_filename}
      className="inline-flex items-center justify-center w-10 h-10 rounded border bg-neutral-50 text-xs font-mono"
    >
      PDF
    </a>
  );
}

export function ExpenseList({ defaultFilters = {} }: Props) {
  const [filters, setFilters] = useState<ExpenseListFilters>({
    limit: 50,
    offset: 0,
    ...defaultFilters,
  });
  const { data, loading, error, refetch } = useExpenses(filters);
  const { data: categories } = useCategories();

  const update = (patch: Partial<ExpenseListFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <label className="flex flex-col text-xs">
          <span className="text-neutral-600">Desde</span>
          <input
            type="date"
            value={filters.date_from || ''}
            onChange={(e) => update({ date_from: e.target.value || undefined })}
            className="border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs">
          <span className="text-neutral-600">Hasta</span>
          <input
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => update({ date_to: e.target.value || undefined })}
            className="border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs">
          <span className="text-neutral-600">Categoría</span>
          <select
            value={filters.category_id || ''}
            onChange={(e) => update({ category_id: e.target.value || undefined })}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">— Todas —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs">
          <span className="text-neutral-600">Status</span>
          <select
            value={filters.status || ''}
            onChange={(e) => update({ status: (e.target.value as ExpenseStatus) || undefined })}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">— Cualquiera —</option>
            <option value="draft">Borrador</option>
            <option value="recorded">Registrado</option>
            <option value="paid">Pagado</option>
            <option value="void">Anulado</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => void refetch()}
          className="px-3 py-1 text-sm border rounded"
        >
          Actualizar
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600">
          Error: {error} · <button onClick={refetch} className="underline">Reintentar</button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Descripción</th>
              <th className="px-3 py-2">Categoría</th>
              <th className="px-3 py-2 text-right">Monto</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Recibos</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr><td colSpan={6} className="px-3 py-4 text-neutral-500">Cargando…</td></tr>
            ) : !data || data.items.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-4 text-neutral-500">Sin gastos en el rango.</td></tr>
            ) : (
              data.items.map((e) => (
                <tr key={e.id} className="border-b hover:bg-neutral-50">
                  <td className="px-3 py-2 whitespace-nowrap">{e.expense_date}</td>
                  <td className="px-3 py-2">{e.description}</td>
                  <td className="px-3 py-2">{e.category_name}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {e.amount} {e.currency}
                  </td>
                  <td className="px-3 py-2">{STATUS_LABEL[e.status]}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      {e.receipts.map((r) => (
                        <ReceiptThumb key={r.id} receipt={r} />
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {data && (
          <div className="px-3 py-2 text-xs text-neutral-500">
            Total: {data.count} · página {data.offset / Math.max(1, data.limit) + 1}
          </div>
        )}
      </div>
    </div>
  );
}
